import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useMixPlayback, canPlayMix, useLazyMixResolveMode } from './DJPal/MixPlayer';
import { useToast } from '../contexts/ToastContext';
import { likeMix, unlikeMix } from '../redux/thunk/likedMixesThunk';
import AddToPlaylistModal from './AddToPlaylistModal';
import { resolveMixCover } from '../utils/nativeMixCover';
import { track as trackEvent } from '../utils/analytics';
import { positionMenuAtPoint } from '../utils/menuPosition';
import { publicEntityId } from '../utils/entityId';
import './TrackContextMenu.scss';

// Mix.link → playable identifiers for the playlist item. A mix enters the
// playlist as a (very long) track-shaped row and plays through the same
// YouTube/SoundCloud resolution as any linked track.
const ytIdFromLink = (link) => {
    const m = /(?:youtube\.com\/(?:watch\?v=|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/.exec(link || '');
    return m ? m[1] : null;
};

// `openAtPoint` ({ x, y, nonce } | null): opens THIS menu as an OS-style
// right-click context menu anchored at the given viewport point instead of
// the ⋯ button — same contract as TrackContextMenu. The host row's
// onContextMenu bumps `nonce` on every right-click so repeat clicks at the
// same spot re-open it.
// `hideLike`: suppress the Like item — for the page-header ⋯, where the
// prominent MixLikeButton is always visible right next to it (row menus keep
// it: on mobile rows the menu is the only Like path).
const MixContextMenu = ({ mix, openAtPoint = null, hideLike = false }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [showPlaylistModal, setShowPlaylistModal] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState(null);
    // Set when the menu was opened via right-click — positions the dropdown
    // at the cursor instead of the ⋯ button. Cleared on close/button-opens.
    const [pointAnchor, setPointAnchor] = useState(null);
    const menuRef = useRef(null);
    const btnRef = useRef(null);
    // Cross-menu contract: opening ANY context menu broadcasts its id; every
    // other open menu closes. The wrappers stopPropagation on pointerdown
    // (to protect row clicks), which also swallowed the outside-click that
    // used to close a sibling menu — two menus could sit open at once.
    const menuIdRef = useRef(`mix-${Math.random().toString(36).slice(2)}`);
    const toast = useToast();
    const { queueMix } = useMixPlayback();
    // Re-render when LAZY_MIX_RESOLVE_MODE loads/flips so canPlayMix() below
    // re-evaluates (the documented subscription pattern for canPlayMix users).
    useLazyMixResolveMode();
    const dispatch = useDispatch();
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
    const likedMixSet = useSelector(state => state.likedMixes.likedMixSet);
    const djName = mix.djName || mix.artist;
    const likeKey = mix.title && djName ? `${mix.title}|||${djName}` : null;
    const isLiked = likeKey ? likedMixSet[likeKey] : false;

    // Portal-position the dropdown to dodge .djpal-mix-card's overflow:hidden
    // (which would otherwise clip the menu inside the card). Mirrors
    // TrackContextMenu's pattern: useLayoutEffect runs before paint so the
    // menu never flashes at an off-screen default before fixed coords apply.
    useLayoutEffect(() => {
        if (!menuOpen || !btnRef.current || pointAnchor) {
            if (!menuOpen) setDropdownStyle(null);
            return;
        }
        const rect = btnRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const dropdownHeight = 190; // 4 menu items, approximate
        const dropdownWidth = 200; // min-width 190 + padding
        const openUp = spaceBelow < dropdownHeight;
        // Anchor to the button's right edge (dropdown extends left) unless the
        // button sits too close to the left edge to fit it — then anchor left
        // (extends right). On mobile the mix-page header ⋯ lives at the far
        // LEFT of its row, so the old always-extend-left put the whole menu
        // off-screen.
        const openLeft = rect.right >= dropdownWidth;
        setDropdownStyle({
            position: 'fixed',
            ...(openLeft
                ? { right: window.innerWidth - rect.right, left: 'auto' }
                : { left: rect.left, right: 'auto' }),
            ...(openUp
                ? { bottom: window.innerHeight - rect.top + 4, top: 'auto' }
                : { top: rect.bottom + 4, bottom: 'auto' }),
            zIndex: 2000,
        });
    }, [menuOpen, pointAnchor]);

    // Right-click positioning: the menu renders hidden at the raw click point
    // first, gets measured, then positionMenuAtPoint flips/clamps it on-screen
    // before paint (the documented utils/menuPosition pattern).
    useLayoutEffect(() => {
        if (!menuOpen || !pointAnchor || !menuRef.current) return;
        const { left, top } = positionMenuAtPoint(menuRef.current, pointAnchor.x, pointAnchor.y);
        setDropdownStyle({ position: 'fixed', left, top, right: 'auto', bottom: 'auto', zIndex: 2000 });
    }, [menuOpen, pointAnchor]);

    const closeMenu = useCallback(() => {
        setMenuOpen(false);
        setPointAnchor(null);
    }, []);

    const handleToggleMenu = (e) => {
        e.stopPropagation();
        e.preventDefault();
        setPointAnchor(null);
        setMenuOpen(prev => {
            if (!prev) {
                window.dispatchEvent(new CustomEvent('djpal-context-menu-open', { detail: menuIdRef.current }));
                trackEvent('mix_context_menu_opened', { trigger: 'kebab' });
            }
            return !prev;
        });
    };

    // Right-click open: the host row bumps openAtPoint.nonce per contextmenu
    // event; each bump opens this menu at that point (same cross-menu-close
    // broadcast as the button path).
    const lastPointNonceRef = useRef(null);
    useEffect(() => {
        if (!openAtPoint || openAtPoint.nonce === lastPointNonceRef.current) return;
        lastPointNonceRef.current = openAtPoint.nonce;
        window.dispatchEvent(new CustomEvent('djpal-context-menu-open', { detail: menuIdRef.current }));
        setPointAnchor({ x: openAtPoint.x, y: openAtPoint.y });
        setMenuOpen(true);
        trackEvent('mix_context_menu_opened', { trigger: 'right_click' });
    }, [openAtPoint]);

    useEffect(() => {
        const onOtherMenu = (e) => { if (e.detail !== menuIdRef.current) closeMenu(); };
        window.addEventListener('djpal-context-menu-open', onOtherMenu);
        return () => window.removeEventListener('djpal-context-menu-open', onOtherMenu);
    }, [closeMenu]);

    const handleAddToPlaylist = (e) => {
        e.stopPropagation();
        trackEvent('mix_add_to_playlist_opened', { mix_title: mix.title || null, dj_name: djName || null });
        setMenuOpen(false);
        setShowPlaylistModal(true);
    };

    const handleAddToNextUp = (e) => {
        e.stopPropagation();
        // No toast here — queueMix resolves the stream async and raises its own
        // "Added to Next Up" / error toast, so an optimistic one would double up
        // (and claim success before the add can still fail).
        queueMix(mix);
        setMenuOpen(false);
    };

    const handleCopyMixName = (e) => {
        e.stopPropagation();
        const text = djName ? `${djName} - ${mix.title}` : mix.title;
        navigator.clipboard.writeText(text).then(() => {
            toast.success('Copied');
        }).catch(() => {
            toast.error('Failed to copy');
        });
        setMenuOpen(false);
    };

    const handleToggleLike = (e) => {
        e.stopPropagation();
        if (!isAuthenticated || !likeKey) return;
        if (isLiked) {
            dispatch(unlikeMix({ title: mix.title, djName }));
        } else {
            dispatch(likeMix(mix));
        }
        setMenuOpen(false);
    };

    useEffect(() => {
        if (!menuOpen) return;
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target) &&
                btnRef.current && !btnRef.current.contains(e.target)) {
                closeMenu();
            }
        };
        const handleEsc = (e) => {
            if (e.key === 'Escape') closeMenu();
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEsc);
        };
    }, [menuOpen, closeMenu]);

    return (
        <div className="track-more-wrapper">
            <button
                ref={btnRef}
                className="track-more-btn"
                title="More options"
                onClick={handleToggleMenu}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="5" cy="12" r="2.5" />
                    <circle cx="12" cy="12" r="2.5" />
                    <circle cx="19" cy="12" r="2.5" />
                </svg>
            </button>

            {menuOpen && (dropdownStyle || pointAnchor) && ReactDOM.createPortal(
                <div
                    className="track-context-dropdown"
                    ref={menuRef}
                    // Point-opens render hidden at the raw click point for one
                    // layout pass so positionMenuAtPoint can measure them.
                    // right/bottom MUST be cleared — the SCSS defaults to
                    // right:0, and left+right together stretch the menu to the
                    // viewport edge (see TrackContextMenu).
                    style={dropdownStyle || { position: 'fixed', left: pointAnchor.x, top: pointAnchor.y, right: 'auto', bottom: 'auto', visibility: 'hidden', zIndex: 2000 }}
                    onClick={e => e.stopPropagation()}
                >
                    {/* Like shows on desktop too (2026-07 consistency pass:
                        Like is reachable from every track/mix menu, like
                        Spotify). The hover MixLikeButton on rows stays — the
                        duplication is deliberate. Page headers pass hideLike:
                        their always-visible like button makes this redundant. */}
                    {!hideLike && isAuthenticated && likeKey && (
                        <div className="dropdown-item" onClick={handleToggleLike}>
                            <span className="dropdown-icon">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                                </svg>
                            </span>
                            {isLiked ? 'Unlike mix' : 'Like mix'}
                        </div>
                    )}
                    <div className="dropdown-item" onClick={handleAddToPlaylist}>
                        <span className="dropdown-icon">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="3" y1="7" x2="16" y2="7" />
                                <line x1="3" y1="12" x2="16" y2="12" />
                                <line x1="3" y1="17" x2="12" y2="17" />
                                <line x1="18" y1="14" x2="18" y2="22" />
                                <line x1="14" y1="18" x2="22" y2="18" />
                            </svg>
                        </span>
                        Add to playlist
                    </div>
                    {/* Only where the mix can actually play: canPlayMix is
                        false on native (mixes never play on iOS/Android), for
                        takedowns, and for unresolvable links — queueing any of
                        those could only end in an error toast. */}
                    {canPlayMix(mix) && (
                        <div className="dropdown-item" onClick={handleAddToNextUp}>
                            <span className="dropdown-icon">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="12" y1="5" x2="12" y2="19" />
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                </svg>
                            </span>
                            Add to next up
                        </div>
                    )}
                    <div className="dropdown-item" onClick={handleCopyMixName}>
                        <span className="dropdown-icon">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                            </svg>
                        </span>
                        Copy mix name
                    </div>
                </div>,
                document.body
            )}

            {showPlaylistModal && (
                <AddToPlaylistModal
                    show={showPlaylistModal}
                    onHide={() => setShowPlaylistModal(false)}
                    track={{
                        title: mix.title || 'Unknown Mix',
                        artist: djName || '',
                        // Deliberately NO publicId — that field means a music-info
                        // TRACK id downstream (recommendation resolution); a mix id
                        // there would poison it. The mix identity travels in its
                        // own field so the playlist row knows it's a MIX and opens
                        // the mix page instead of running a doomed track search.
                        // publicEntityId, NOT entityId: stale snapshots and
                        // pre-08-12 shapes can still carry a numeric row id in
                        // `.id`, which the server's catalogIdOrNull guard
                        // would silently null into an identity-less row.
                        mixPublicId: publicEntityId(mix),
                        artwork: resolveMixCover(mix) || mix.artworkUrl || mix.artwork || '',
                        youtubeId: ytIdFromLink(mix.link),
                        soundcloudUrl: (mix.link || '').includes('soundcloud.com') ? mix.link : null,
                    }}
                />
            )}
        </div>
    );
};

export default MixContextMenu;
